/**
 * Reads when a file was recorded from its own metadata, without decoding it:
 *  - photos: EXIF `DateTimeOriginal` (JPEG APP1, PNG `eXIf`, WebP `EXIF`, or a bare TIFF block);
 *  - MP4/MOV: the movie header (`mvhd`) creation time, which cameras and phones set and which
 *    mediabunny's metadata tags don't expose.
 * Pure functions over bytes so they can be unit-tested without a browser.
 */

/** Reads `length` bytes at `offset` (fewer at the end of the file). */
export type ByteReader = (offset: number, length: number) => Promise<Uint8Array>;

export function blobReader(blob: Blob): ByteReader {
  return async (offset, length) => new Uint8Array(await blob.slice(offset, offset + length).arrayBuffer());
}

/** Dates before this are placeholders (unset camera clocks write 1904 or 1970), not real recordings. */
const EARLIEST_PLAUSIBLE = Date.UTC(1980, 0, 1);

function plausible(date: Date): Date | undefined {
  const t = date.getTime();
  return Number.isFinite(t) && t >= EARLIEST_PLAUSIBLE && t <= Date.now() + 366 * 86_400_000
    ? date
    : undefined;
}

// ---------------------------------------------------------------------------------------------
// EXIF
// ---------------------------------------------------------------------------------------------

const TAG_EXIF_IFD = 0x8769;
const TAG_DATE_TIME = 0x0132;
const TAG_DATE_TIME_ORIGINAL = 0x9003;
const TAG_DATE_TIME_DIGITIZED = 0x9004;
const TAG_OFFSET_TIME = 0x9010;
const TAG_OFFSET_TIME_ORIGINAL = 0x9011;
const TAG_OFFSET_TIME_DIGITIZED = 0x9012;

const ascii = (bytes: Uint8Array, start: number, length: number) =>
  String.fromCharCode(...bytes.subarray(start, start + length));

/** Finds the TIFF (EXIF) block inside a JPEG, PNG or WebP file, or accepts a bare TIFF block. */
export function findExifBlock(bytes: Uint8Array): Uint8Array | undefined {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const tiffAt = (start: number) => (isTiff(bytes, start) ? bytes.subarray(start) : undefined);

  // JPEG: walk marker segments up to the image data.
  if (bytes[0] === 0xff && bytes[1] === 0xd8) {
    let pos = 2;
    while (pos + 4 <= bytes.length && bytes[pos] === 0xff) {
      const marker = bytes[pos + 1]!;
      if (marker === 0xda || marker === 0xd9) break; // start of scan / end of image
      const length = view.getUint16(pos + 2);
      if (marker === 0xe1 && ascii(bytes, pos + 4, 6) === 'Exif\0\0') {
        return tiffAt(pos + 10)?.subarray(0, length - 8);
      }
      pos += 2 + length;
    }
    return undefined;
  }
  // PNG: length/type chunks after the 8-byte signature.
  if (ascii(bytes, 1, 3) === 'PNG') {
    let pos = 8;
    while (pos + 8 <= bytes.length) {
      const length = view.getUint32(pos);
      const type = ascii(bytes, pos + 4, 4);
      if (type === 'eXIf') return tiffAt(pos + 8)?.subarray(0, length);
      if (type === 'IEND') break;
      pos += 12 + length;
    }
    return undefined;
  }
  // WebP: RIFF chunks (little-endian sizes, padded to even).
  if (ascii(bytes, 0, 4) === 'RIFF' && ascii(bytes, 8, 4) === 'WEBP') {
    let pos = 12;
    while (pos + 8 <= bytes.length) {
      const type = ascii(bytes, pos, 4);
      const length = view.getUint32(pos + 4, true);
      if (type === 'EXIF') {
        // Some writers keep the JPEG-style "Exif\0\0" prefix.
        const start = ascii(bytes, pos + 8, 6) === 'Exif\0\0' ? pos + 14 : pos + 8;
        return tiffAt(start);
      }
      pos += 8 + length + (length & 1);
    }
    return undefined;
  }
  // HEIF: the Exif item's payload ("Exif\0\0" then the TIFF header) sits somewhere in the file.
  if (ascii(bytes, 4, 4) === 'ftyp') {
    for (let pos = bytes.indexOf(0x45); pos !== -1; pos = bytes.indexOf(0x45, pos + 1)) {
      if (ascii(bytes, pos, 6) === 'Exif\0\0' && isTiff(bytes, pos + 6)) return bytes.subarray(pos + 6);
    }
    return undefined;
  }
  return tiffAt(0);
}

function isTiff(bytes: Uint8Array, start: number): boolean {
  const sig = ascii(bytes, start, 4);
  return sig === 'II*\0' || sig === 'MM\0*';
}

/** Reads ASCII tags from one IFD. Returns tag → string, plus the Exif sub-IFD offset if present. */
function readIfd(
  tiff: Uint8Array,
  offset: number,
  little: boolean,
): { tags: Map<number, string>; exif?: number } {
  const view = new DataView(tiff.buffer, tiff.byteOffset, tiff.byteLength);
  const tags = new Map<number, string>();
  let exif: number | undefined;
  if (offset + 2 > tiff.length) return { tags };
  const count = view.getUint16(offset, little);
  for (let i = 0; i < count; i++) {
    const entry = offset + 2 + i * 12;
    if (entry + 12 > tiff.length) break;
    const tag = view.getUint16(entry, little);
    const type = view.getUint16(entry + 2, little);
    const n = view.getUint32(entry + 4, little);
    if (tag === TAG_EXIF_IFD) exif = view.getUint32(entry + 8, little);
    else if (type === 2 /* ASCII */ && n > 0) {
      const at = n <= 4 ? entry + 8 : view.getUint32(entry + 8, little);
      if (at + n <= tiff.length) tags.set(tag, ascii(tiff, at, n).replace(/\0.*$/s, '').trim());
    }
  }
  return exif === undefined ? { tags } : { tags, exif };
}

/**
 * Parses an EXIF date ("YYYY:MM:DD HH:MM:SS") with an optional offset ("+02:00"). Without an
 * offset the time is the camera's local time, which we take to be the viewer's time zone.
 */
export function parseExifDateTime(value: string, offset?: string): Date | undefined {
  const m = /^(\d{4})[:-](\d{2})[:-](\d{2})[ T](\d{2}):(\d{2}):(\d{2})/.exec(value);
  if (!m) return undefined;
  const [, y, mo, d, h, mi, s] = m.map(Number) as [number, number, number, number, number, number, number];
  if (offset && /^[+-]\d{2}:\d{2}$/.test(offset)) {
    const pad = (v: number) => String(v).padStart(2, '0');
    return plausible(new Date(`${y}-${pad(mo)}-${pad(d)}T${pad(h)}:${pad(mi)}:${pad(s)}${offset}`));
  }
  return plausible(new Date(y, mo - 1, d, h, mi, s));
}

/** The capture date stored in a photo's EXIF block, if any. */
export function exifCaptureDate(bytes: Uint8Array): Date | undefined {
  const tiff = findExifBlock(bytes);
  if (!tiff || tiff.length < 8) return undefined;
  const little = tiff[0] === 0x49;
  const view = new DataView(tiff.buffer, tiff.byteOffset, tiff.byteLength);
  const ifd0 = readIfd(tiff, view.getUint32(4, little), little);
  const exif = ifd0.exif !== undefined ? readIfd(tiff, ifd0.exif, little).tags : new Map<number, string>();
  const candidates: Array<[string | undefined, string | undefined]> = [
    [exif.get(TAG_DATE_TIME_ORIGINAL), exif.get(TAG_OFFSET_TIME_ORIGINAL)],
    [exif.get(TAG_DATE_TIME_DIGITIZED), exif.get(TAG_OFFSET_TIME_DIGITIZED)],
    [ifd0.tags.get(TAG_DATE_TIME), exif.get(TAG_OFFSET_TIME)],
  ];
  for (const [value, offset] of candidates) {
    const date = value ? parseExifDateTime(value, offset) : undefined;
    if (date) return date;
  }
  return undefined;
}

// ---------------------------------------------------------------------------------------------
// ISO-BMFF (MP4 / MOV)
// ---------------------------------------------------------------------------------------------

/** Seconds between 1904-01-01 (the ISO-BMFF epoch) and 1970-01-01. */
const MP4_EPOCH_OFFSET = 2_082_844_800;

interface Box {
  type: string;
  start: number;
  headerSize: number;
  size: number;
}

async function readBox(read: ByteReader, start: number, end: number): Promise<Box | undefined> {
  if (start + 8 > end) return undefined;
  const head = await read(start, 16);
  if (head.length < 8) return undefined;
  const view = new DataView(head.buffer, head.byteOffset, head.byteLength);
  let size = view.getUint32(0);
  let headerSize = 8;
  if (size === 1) {
    if (head.length < 16) return undefined;
    size = Number(view.getBigUint64(8));
    headerSize = 16;
  } else if (size === 0) size = end - start; // box extends to the end
  if (size < headerSize) return undefined;
  return { type: ascii(head, 4, 4), start, headerSize, size };
}

async function findBox(read: ByteReader, type: string, start: number, end: number): Promise<Box | undefined> {
  // A bounded walk: top-level boxes are few, but a corrupt file must not loop forever.
  for (let pos = start, i = 0; pos < end && i < 10_000; i++) {
    const box = await readBox(read, pos, end);
    if (!box) return undefined;
    if (box.type === type) return box;
    pos += box.size;
  }
  return undefined;
}

/** The creation time in an MP4/MOV movie header, if the file is ISO-BMFF and has a real one. */
export async function mp4CreationDate(read: ByteReader, fileSize: number): Promise<Date | undefined> {
  const first = await readBox(read, 0, fileSize);
  if (!first || !['ftyp', 'moov', 'mdat', 'wide', 'free', 'skip'].includes(first.type)) return undefined;
  const moov = await findBox(read, 'moov', 0, fileSize);
  if (!moov) return undefined;
  const mvhd = await findBox(read, 'mvhd', moov.start + moov.headerSize, moov.start + moov.size);
  if (!mvhd) return undefined;
  const body = await read(mvhd.start + mvhd.headerSize, 12);
  if (body.length < 8) return undefined;
  const view = new DataView(body.buffer, body.byteOffset, body.byteLength);
  const seconds = body[0] === 1 && body.length >= 12 ? Number(view.getBigUint64(4)) : view.getUint32(4);
  if (seconds === 0) return undefined;
  return plausible(new Date((seconds - MP4_EPOCH_OFFSET) * 1000));
}
